// Map marker zoom-tier transition (#431).
//
// The map swaps a marker's DOM element when it crosses a zoom tier (dot -> icon -> icon+label).
// The first implementation replaced the element outright and, on the way up, ran a `markerPop`
// keyframe with a staggered `${index * 30}ms` delay and `animation-fill-mode: both`. `both`
// applies the 0% frame — `scale(0); opacity: 0` — DURING that delay, so for up to 500ms the
// marker was present in the DOM and invisible: the owner saw dot -> blank -> icon. Zooming DOWN
// a tier had no animation at all, so that direction was a hard cut.
//
// This module owns the replacement instead. The outgoing element is NOT removed when the
// incoming one is inserted: it stays in the DOM, absolutely centred over the same anchor point,
// and fades out while the incoming element fades in. There is therefore no frame in which the
// marker has neither the old content nor the new one, in EITHER direction. Nothing is delayed,
// so no marker is ever invisible while it waits its turn.
//
// It is a separate module, not a method on the marker record, because the record class is
// defined inside a React effect in src/App.js and cannot be imported by a test.

// Total transition time. The issue caps the whole thing at ~250ms; 180ms crossfade reads as
// continuous without holding the old content on screen long enough to look like a double image.
export const MARKER_TIER_FADE_MS = 180;

// Slack added to the removal timer so the ghost is never pulled out from under its own final
// frame on a busy main thread.
const GHOST_REMOVAL_SLACK_MS = 40;

// The outgoing element is parked with ONE `cssText` assignment and restored with one more, not
// with eight individual property writes. A tier crossing touches every marker on screen at once,
// so this runs a few hundred times in a burst — and the burst lands in the middle of the same
// zoom gesture #337/#339 spent a ticket making cheap. One string write per element per side is
// measurably less style work than eight.
const GHOST_PARK_STYLE = 'position:absolute;left:50%;top:50%;z-index:0;pointer-events:none;';

// Keyframes for both halves of the crossfade, injected into the map screen's <style> block.
//
// The two halves are deliberately NOT a symmetric crossfade. A symmetric one dips: at the
// midpoint both elements sit near 50% opacity and the marker reads as a half-faded smudge —
// measured at worst-opacity 0.535 in the first browser QA run of this fix. Instead the outgoing
// element (which paints ON TOP, since it is appended after the live one) holds full opacity
// while the incoming element fades in behind it, and only then dissolves. Something on this
// marker is therefore fully painted in every frame of the transition, measured worst-opacity
// 1.0, which is as literal an answer to "they don't disappear" as the DOM can give.
//
// `markerTierGhostOut` animates opacity and NOTHING else, on purpose. An animated `transform`
// here would have to re-state the parked element's centring `translate(-50%, -50%)` in every
// keyframe (an animation replaces the element's own transform outright, so a bare `scale()`
// would drop the centring and the outgoing content would jump off its anchor) — and it would
// promote a second compositor layer for every marker on screen for the length of the crossing.
// The incoming element carries the scale; the outgoing one just dissolves.
export const MARKER_TIER_KEYFRAMES = `@keyframes markerTierIn { from { opacity: 0; transform: scale(0.86); } 45% { opacity: 1; transform: scale(1.03); } to { opacity: 1; transform: none; } } @keyframes markerTierGhostOut { 0%, 45% { opacity: 1; } to { opacity: 0; } }`;

// One MediaQueryList for the whole marker layer rather than a `matchMedia` call per marker per
// tier change (a tier change touches every attached marker at once). Falls back to "no
// preference" where matchMedia is missing.
let reducedMotionQuery;
export const prefersReducedMotion = () => {
  if (reducedMotionQuery === undefined) {
    reducedMotionQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  }
  return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
};

// Test seam only — lets a test flip the emulated preference between cases.
export const resetReducedMotionCache = () => { reducedMotionQuery = undefined; };

const DEFAULT_SCHEDULER = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
};

/**
 * Settle a crossfade that is still running: cancel its removal timer, detach the outgoing
 * element and hand it back with the inline styles this module gave it removed, and clear the
 * incoming element's own fade-in, so BOTH elements are safe to mount again as live content.
 * Safe to call with `null`, and safe to call twice.
 *
 * Clearing the incoming element is not tidiness (cold review finding). Per-tier elements are
 * cached and remounted, `markerTierIn` is declared with `fill-mode: both`, and re-inserting an
 * element restarts its animation — so an element released mid-crossfade (the marker was culled
 * out of the viewport, say) would replay the fade from opacity 0 on its next mount, with no
 * outgoing element behind it. That is exactly the blank frame this module exists to prevent,
 * and it is the same defect the `markerPop` implementation had.
 */
export function clearMarkerTierGhost(handle, scheduler = DEFAULT_SCHEDULER) {
  if (!handle) return;
  if (handle.timer != null) {
    scheduler.clearTimeout(handle.timer);
    handle.timer = null;
  }
  const incoming = handle.incoming;
  handle.incoming = null;
  if (incoming) incoming.style.animation = '';
  const ghost = handle.ghost;
  handle.ghost = null;
  if (!ghost) return;
  if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
  ghost.style.cssText = handle.ghostCssText || '';
}

/**
 * Put `incoming` on screen as the marker's live content.
 *
 * Invariant the map relies on: the live element is always `div.firstChild`, and an outgoing
 * element is only ever an extra, non-interactive child appended after it. `div` therefore keeps
 * its box (and so its `translate(-50%, -50%)` anchor) from the incoming element from the first
 * frame of the swap.
 *
 * @returns {{ghost: Element, incoming: Element, timer: any}|null} handle for an in-flight
 *   crossfade, or null when the swap was instant. Pass it back as `options.handle` on the next
 *   call, and settle it with clearMarkerTierGhost if the marker goes away first.
 */
export function swapMarkerTierContent(div, incoming, options = {}) {
  const {
    handle = null,
    animate = false,
    reducedMotion = false,
    durationMs = MARKER_TIER_FADE_MS,
    scheduler = DEFAULT_SCHEDULER,
  } = options;
  if (!div || !incoming) return null;

  // Any crossfade still running is settled BEFORE the next one starts, so repeated zoom_changed
  // events can never stack ghosts or strand one in the DOM. This is what keeps the whole swap
  // idempotent, which the map depends on: updateContent runs on every zoom_changed and again
  // after every palette swap.
  clearMarkerTierGhost(handle, scheduler);

  const outgoing = div.firstChild;
  if (outgoing === incoming) return null;

  incoming.style.animation = '';
  incoming.style.opacity = '';
  if (outgoing) div.replaceChild(incoming, outgoing);
  else div.appendChild(incoming);

  // Nothing to fade from (first paint of this marker), or motion is not wanted: the element is
  // already mounted and visible, which is the instant swap.
  if (!outgoing || !animate || reducedMotion || durationMs <= 0) return null;

  // Parked over the same anchor point the wrapper is centred on, and never able to eat the click
  // that belongs to the marker underneath it. `pointer-events` is in the parked cssText.
  const ghostCssText = outgoing.style.cssText;
  outgoing.style.cssText = `${ghostCssText}${GHOST_PARK_STYLE}transform:translate(-50%, -50%);animation:markerTierGhostOut ${durationMs}ms ease-out both;`;
  div.appendChild(outgoing);

  // No delay on either half. A stagger here is exactly what made markers invisible.
  incoming.style.animation = `markerTierIn ${durationMs}ms ease-out both`;

  const nextHandle = { ghost: outgoing, ghostCssText, incoming, timer: null };
  nextHandle.timer = scheduler.setTimeout(() => {
    nextHandle.timer = null;
    clearMarkerTierGhost(nextHandle, scheduler);
  }, durationMs + GHOST_REMOVAL_SLACK_MS);
  return nextHandle;
}

// --- Which markers are worth animating -------------------------------------------------------
//
// A tier crossing touches every attached marker at once, and culling keeps records attached well
// outside the viewport, so animating all of them is what turned one crossing into a 50ms long
// task under 4x CPU throttle — a regression in the very zoom-out gesture #337/#339 spent a
// ticket making cheap. The layer therefore animates only markers near the viewport. The box is
// PADDED because a marker's anchor can sit outside the bounds while the marker still paints
// inside them (a label-tier marker is ~120px wide), and because `getBounds()` during a zoom
// already reports the destination rather than the frame on screen.

/**
 * @param {{getNorthEast: Function, getSouthWest: Function}|null} bounds google.maps.LatLngBounds
 * @param {number} pad fraction of the viewport span to add on each side, per axis
 * @returns {{minLat: number, maxLat: number, minLng: number, maxLng: number}|null} null when
 *   there are no bounds to work from — the caller then animates everything, which is the same
 *   behaviour as before this bound existed and never suppresses a marker wrongly.
 */
export function viewportAnimationBox(bounds, pad) {
  if (!bounds || typeof bounds.getNorthEast !== 'function' || typeof bounds.getSouthWest !== 'function') return null;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  if (!ne || !sw) return null;
  const latPad = (ne.lat() - sw.lat()) * pad;
  const lngPad = (ne.lng() - sw.lng()) * pad;
  return {
    minLat: sw.lat() - latPad,
    maxLat: ne.lat() + latPad,
    minLng: sw.lng() - lngPad,
    maxLng: ne.lng() + lngPad,
  };
}

/** Inclusive on every edge — a marker exactly on the boundary is on screen. */
export function isInAnimationBox(position, box) {
  if (!box) return true;
  if (!position) return false;
  const { lat, lng } = position;
  return lat >= box.minLat && lat <= box.maxLat && lng >= box.minLng && lng <= box.maxLng;
}
